import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { TimeService } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { QuestionService } from '@tellent-ask-hr/dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormWrapperConfig,
  UnwrapSignal,
} from '@tellent-ask-hr/dynamic-forms-types';
import type { DateTemplateItem } from '@tellent-ask-hr/templates-types';

export class DateConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionService = inject(QuestionService);
  private readonly i18n = inject(I18nService);
  private readonly timeService = inject(TimeService);

  public readonly kind: DateTemplateItem['type'] = 'date';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.date'),
    icon: 'calendar-today',
  };

  public readonly components = {
    define: () => import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.QuestionComponent),
    respond: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.RespondDateComponent),
    present: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (
    data?: DefineFormData,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').QuestionComponent['defineForm']
  > => this.questionService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondFormWrapperConfig,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondDateComponent['respondConfig']
  > => ({
    ...data,
    placeholder: this.i18n.translate('tlc.ask_hr.form_template_create.configs.select_date'),
  });

  public readonly respondForm = (config: {
    required: boolean;
  }): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondDateComponent['respondForm']
  > =>
    this.formBuilder.group({
      value: this.formBuilder.control<string>('', config.required ? Validators.required : []),
    });

  public readonly presentConfig = (
    data: DateTemplateItem,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value ? this.timeService.format({ input: data.value, pattern: 'lll' }) : null,
    kind: {
      type: 'date',
      format: 'DD MMM YYYY',
    },
  });
}
